import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  ReactNode,
  useCallback,
  useRef,
  useSyncExternalStore,
} from 'react';
import {
  Rive,
  EventType,
  Event as RiveEvent,
  EventCallback,
  RiveResetParameters,
  Layout,
  Alignment,
  Fit,
} from '@rive-app/webgl2';
import {useDangerousHooks} from '@packages/systems/wfdl';
import {emptyArray} from '@packages/utilities/fp/utils';
import {useNativeNode} from '../useNativeNode';

type ArtboardContents = NonNullable<Rive['contents']['artboards']>[0];
type StateMachineContents = ArtboardContents['stateMachines'][0];
type Contents = Rive['contents'];

/**
 * Types for the hook's return value.
 */
interface UseRiveReturn {
  rive: RiveState;
  isInstanceReady: boolean;
  hasInstance: boolean;
  play: (animationNames?: string | string[]) => void;
  pause: (animationNames?: string | string[]) => void;
  stop: (animationNames?: string | string[]) => void;
  reset: (params?: RiveResetParameters) => void;
  setIsTouchScrollEnabled: (isTouchScrollEnabled: boolean) => void;
  setOnAutomaticallyHandleEventsChange: (
    automaticallyHandleEventsChange: boolean
  ) => void;
  setAutoPlay: (autoPlay: boolean) => void;
  setSpeed: (speed: number) => void;
  setFrame: (frame: number) => void;
  selectFit: (value: string) => void;
  selectAlignment: (value: string) => void;
  selectArtboard: (artboardName: string) => void;
  selectStateMachine: (stateMachineName: string) => void;
  activeArtboard: ArtboardContents | null;
  activeStateMachine: StateMachineContents | null;
  artboards: ArtboardContents[];
  stateMachines: StateMachineContents[];
}

/**
 * Snapshot of the current Rive state.
 */
interface RiveState {
  isPlaying: boolean;
  currentState: string | null;
  activeAnimations: string[];
  loopCount: number;
  currentFrame: number;
  totalFrames: number;
  animationProgress: number;
  currentAnimationName: string | null;
  playbackSpeed: number;
  isLooping: boolean;
  artboardWidth: number;
  artboardHeight: number;
  animationDirection: 'forward' | 'reverse';
  lastPlayedAnimation: string | null;
}

const defaultRiveSnapshot: RiveState = {
  currentState: null,
  isPlaying: false,
  activeAnimations: emptyArray,
  loopCount: 0,
  currentFrame: 0,
  totalFrames: 0,
  animationProgress: 0,
  currentAnimationName: null,
  playbackSpeed: 0,
  isLooping: false,
  artboardWidth: 0,
  artboardHeight: 0,
  animationDirection: 'forward',
  lastPlayedAnimation: null,
};

/**
 * Configuration options for the hook.
 */
interface UseRiveOptions {
  onPlay?: EventCallback;
  onPause?: EventCallback;
  onStop?: EventCallback;
  onLoop?: EventCallback;
  onStateChange?: EventCallback;
  onEvent?: EventCallback;
  onLoad?: EventCallback;
  onError?: EventCallback;
  onAdvance?: EventCallback;
  onDraw?: EventCallback;
}

/**
 * Custom React hook to interact with a Rive instance.
 *
 * @param riveInstance - The existing Rive instance to interact with.
 * @param options - Optional configuration for handling Rive events.
 * @returns An object containing the current Rive state and control methods.
 */
export const useRive = (
  {
    riveInstance,
    riveInstanceLoaded,
    artboardValue,
    stateMachineValue,
    onStateMachineChange,
    onArtboardChange,
    onAutoPlayChange,
    onIsTouchScrollEnabledChange,
    onAutomaticallyHandleEventsChange,
    onFitChange,
    onAlignmentChange,
  }: {
    riveInstance: Rive | null;
    riveInstanceLoaded: boolean;
    artboardValue: string | null;
    stateMachineValue: string | null;
    onStateMachineChange: (value: string) => void;
    onArtboardChange: (value: string) => void;
    onFitChange: (value: string) => void;
    onAlignmentChange: (value: string) => void;
    onAutoPlayChange: (value: boolean) => void;
    onIsTouchScrollEnabledChange: (value: boolean) => void;
    onAutomaticallyHandleEventsChange: (value: boolean) => void;
  },
  options: UseRiveOptions = {}
): UseRiveReturn => {
  React.useEffect(
    function resetOnUnmountEffect() {
      return () => {
        try {
          riveInstance?.pause();
        } catch (e) {
          console.error('Error pausing rive instance', e);
        }
      };
    },
    [riveInstance]
  );

  const [tick, setTick] = useState<number>(0);
  const [contents, setContents] = useState<Contents | undefined>(undefined);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const optionsRef = useRef<UseRiveOptions>(options);
  optionsRef.current = options;

  React.useEffect(() => {
    if (riveInstanceLoaded) {
      try {
        setContents(riveInstance?.contents);
      } catch (e) {
        setContents(undefined);
        console.error('Error loading rive instance', e);
      }
    }
  }, [riveInstance, riveInstanceLoaded, tick]);

  const isInstanceReady = riveInstance != null && riveInstanceLoaded;
  const artboards: ArtboardContents[] = isInstanceReady
    ? (contents?.artboards ?? emptyArray)
    : emptyArray;
  const activeArtboard =
    artboards.find((ab) => ab.name === artboardValue) || null;
  const stateMachines: StateMachineContents[] = activeArtboard
    ? activeArtboard.stateMachines
    : emptyArray;
  const activeStateMachine =
    stateMachines.find((sm) => sm.name === stateMachineValue) ?? null;

  // Track whether we've auto-selected for the current file.
  // These refs reset naturally when the RiveProvider remounts on file change
  // (its key includes pathValue.src), so auto-select only fires once per file.
  const hasAutoSelectedArtboardRef = useRef(false);
  const hasAutoSelectedStateMachineRef = useRef(false);

  // Auto-select the first artboard when none is selected or the current
  // selection is stale (e.g. file was replaced and old artboard doesn't exist).
  React.useEffect(() => {
    if (hasAutoSelectedArtboardRef.current) return;
    if (artboards.length === 0) return;
    if (
      artboardValue !== null &&
      artboards.some((ab) => ab.name === artboardValue)
    ) {
      return;
    }
    hasAutoSelectedArtboardRef.current = true;
    onArtboardChange(artboards[0]!.name);
  }, [artboardValue, artboards, onArtboardChange]);

  // Auto-select the first state machine when none is selected or stale.
  React.useEffect(() => {
    if (hasAutoSelectedStateMachineRef.current) return;
    if (stateMachines.length === 0) return;
    if (
      stateMachineValue !== null &&
      stateMachines.some((sm) => sm.name === stateMachineValue)
    ) {
      return;
    }
    hasAutoSelectedStateMachineRef.current = true;
    onStateMachineChange(stateMachines[0]!.name);
  }, [stateMachineValue, stateMachines, onStateMachineChange]);

  const subscribe = React.useCallback(
    (onStoreChange: () => void) => {
      const handleStateChange = (event: RiveEvent) => {
        setTick((v) => (v < 100 ? v + 1 : 0)); // force rerender
        optionsRef.current?.onStateChange?.(event);
        onStoreChange();
      };
      const handlePlay = (event: RiveEvent) => {
        optionsRef.current?.onPlay?.(event);
        onStoreChange();
        setIsPlaying(true);
      };
      const handlePause = (event: RiveEvent) => {
        optionsRef.current?.onPause?.(event);
        setIsPlaying(false);
        onStoreChange();
      };
      const handleStop = (event: RiveEvent) => {
        optionsRef.current?.onStop?.(event);
        onStoreChange();
      };
      const handleLoop = (event: RiveEvent) => {
        optionsRef.current?.onLoop?.(event);
        onStoreChange();
      };
      const handleEvent = (event: RiveEvent) => {
        optionsRef.current?.onEvent?.(event);
        onStoreChange();
      };
      const handleLoad = (event: RiveEvent) => {
        setTick((v) => (v < 100 ? v + 1 : 0)); // force rerender
        optionsRef.current?.onLoad?.(event);
        onStoreChange();
      };
      const handleError = (event: RiveEvent) => {
        optionsRef.current?.onError?.(event);
        onStoreChange();
      };
      const handleAdvance = (event: RiveEvent) => {
        optionsRef.current?.onAdvance?.(event);
        onStoreChange();
      };
      const handleDraw = (event: RiveEvent) => {
        optionsRef.current?.onDraw?.(event);
        onStoreChange();
      };

      riveInstance?.on(EventType.StateChange, handleStateChange);
      riveInstance?.on(EventType.Play, handlePlay);
      riveInstance?.on(EventType.Pause, handlePause);
      riveInstance?.on(EventType.Stop, handleStop);
      riveInstance?.on(EventType.Loop, handleLoop);
      riveInstance?.on(EventType.RiveEvent, handleEvent);
      riveInstance?.on(EventType.Load, handleLoad);
      riveInstance?.on(EventType.LoadError, handleError);
      riveInstance?.on(EventType.Advance, handleAdvance);
      riveInstance?.on(EventType.Draw, handleDraw);

      return () => {
        riveInstance?.off(EventType.StateChange, handleStateChange);
        riveInstance?.off(EventType.Play, handlePlay);
        riveInstance?.off(EventType.Pause, handlePause);
        riveInstance?.off(EventType.Stop, handleStop);
        riveInstance?.off(EventType.Loop, handleLoop);
        riveInstance?.off(EventType.RiveEvent, handleEvent);
        riveInstance?.off(EventType.Load, handleLoad);
        riveInstance?.off(EventType.LoadError, handleError);
        riveInstance?.off(EventType.Advance, handleAdvance);
        riveInstance?.off(EventType.Draw, handleDraw);
      };
    },
    [riveInstance]
  );

  const snapshot = useMemo((): RiveState => {
    if (riveInstance == null) {
      return defaultRiveSnapshot;
    }

    const {playingAnimationNames, frameTimes, frameCount, durations, bounds} =
      riveInstance;

    const activeAnimation = activeArtboard?.animations.find((anim) =>
      playingAnimationNames.includes(anim)
    );

    const progress = activeAnimation
      ? (frameTimes.length / frameCount) * 100
      : 0;

    return {
      isPlaying,
      currentState: activeStateMachine ? activeStateMachine.name : null,
      activeAnimations: playingAnimationNames,
      loopCount: durations.length,
      currentFrame: frameCount || 0,
      totalFrames: frameTimes.length || 0,
      animationProgress: progress,
      currentAnimationName: activeAnimation || null,
      playbackSpeed: frameTimes[0] || 1,
      isLooping: playingAnimationNames.length > 0,
      artboardWidth: bounds?.maxX - bounds?.minX || 0,
      artboardHeight: bounds?.maxY - bounds?.minY || 0,
      animationDirection:
        isPlaying && frameTimes[0] && frameTimes[0] < 0 ? 'reverse' : 'forward',
      lastPlayedAnimation: playingAnimationNames[0] || null,
    };
  }, [riveInstance, activeArtboard, activeStateMachine, isPlaying]);

  const getSnapshot = () => snapshot;

  const rive = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const play = useCallback(() => riveInstance?.play(), [riveInstance]);
  const pause = useCallback(() => riveInstance?.pause(), [riveInstance]);
  const stop = useCallback(() => riveInstance?.stop(), [riveInstance]);
  const reset = useCallback(
    (params?: RiveResetParameters) => riveInstance?.reset(params),
    [riveInstance]
  );
  const selectAlignment = useCallback(
    (value: string) => {
      if (!riveInstance || !value) return;
      onAlignmentChange(value);
      try {
        riveInstance.layout = new Layout({
          ...riveInstance.layout,
          alignment: value as Alignment,
        });
        if (!riveInstance.isPlaying) {
          riveInstance.resizeDrawingSurfaceToCanvas();
        }
      } catch {
        // Rive instance may not be fully initialized (e.g. WASM still loading)
      }
    },
    [onAlignmentChange, riveInstance]
  );
  const selectFit = useCallback(
    (value: string) => {
      if (!riveInstance || !value) return;
      onFitChange(value);
      try {
        // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
        riveInstance.layout = new Layout({
          ...riveInstance.layout,
          fit: value as Fit,
        });
        if (!riveInstance.isPlaying) {
          riveInstance.resizeDrawingSurfaceToCanvas();
        }
      } catch {
        // Rive instance may not be fully initialized (e.g. WASM still loading)
      }
    },
    [onFitChange, riveInstance]
  );
  const setAutoPlay = useCallback(
    (autoPlay: boolean) => {
      onAutoPlayChange(autoPlay);
    },
    [onAutoPlayChange]
  );
  const setIsTouchScrollEnabled = useCallback(
    (enabled: boolean) => {
      onIsTouchScrollEnabledChange(enabled);
    },
    [onIsTouchScrollEnabledChange]
  );
  const setOnAutomaticallyHandleEventsChange = useCallback(
    (automaticallyHandleEvents: boolean) => {
      onAutomaticallyHandleEventsChange(automaticallyHandleEvents);
    },
    [onAutomaticallyHandleEventsChange]
  );
  const setSpeed = useCallback(
    (speed: number) => {
      riveInstance?.scrub(riveInstance?.playingAnimationNames, speed);
    },
    [riveInstance]
  );
  const setFrame = useCallback(
    (frame: number) =>
      riveInstance?.scrub(riveInstance?.playingAnimationNames, frame),
    [riveInstance]
  );

  const selectArtboard = useCallback(
    (artboardName: string) => {
      const artboard = artboards.find((ab) => ab.name === artboardName) || null;
      if (artboard) {
        onArtboardChange(artboardName);
      }
    },
    [artboards, onArtboardChange]
  );

  const selectStateMachine = useCallback(
    (stateMachineName: string) => {
      if (!stateMachines) return;

      const stateMachine = stateMachines.find(
        ({name}) => name === stateMachineName
      );

      if (stateMachine) {
        onStateMachineChange(stateMachineName);
      }
    },
    [stateMachines, onStateMachineChange]
  );

  return {
    rive,
    isInstanceReady,
    hasInstance: riveInstance != null,
    play,
    pause,
    stop,
    reset,
    setSpeed,
    setFrame,
    selectArtboard,
    selectStateMachine,
    activeArtboard,
    activeStateMachine,
    artboards,
    stateMachines,
    setAutoPlay,
    setIsTouchScrollEnabled,
    selectFit,
    selectAlignment,
    setOnAutomaticallyHandleEventsChange,
  };
};

const RiveContext = createContext<UseRiveReturn | undefined>(undefined);

export const RiveProvider: React.FC<{
  children: ReactNode;
  artboardValue: string | null;
  stateMachineValue: string | null;
  onStateMachineChange: (value: string) => void;
  onAutoPlayChange: (value: boolean) => void;
  onIsTouchScrollEnabledChange: (value: boolean) => void;
  onAutomaticallyHandleEventsChange: (value: boolean) => void;
  onArtboardChange: (value: string) => void;
  onFitChange: (value: string) => void;
  onAlignmentChange: (value: string) => void;
}> = ({
  children,
  artboardValue,
  stateMachineValue,
  onStateMachineChange,
  onArtboardChange,
  onIsTouchScrollEnabledChange,
  onAutomaticallyHandleEventsChange,
  onAutoPlayChange,
  onFitChange,
  onAlignmentChange,
}) => {
  const {getNativeNode, getActiveWindow} = useDangerousHooks();
  const frontend = getActiveWindow().window.Webflow.require('rive');
  const nativeNode = useNativeNode();
  const instance =
    nativeNode && nativeNode === getNativeNode()
      ? (frontend?.getInstance(nativeNode) ?? null)
      : null;
  const riveInstance: Rive | null = instance?.rive ?? null;
  const riveInstanceLoaded: boolean =
    instance?.riveInstanceSuccessLoaded ?? false;

  return (
    <RiveContext.Provider
      value={useRive({
        riveInstance,
        riveInstanceLoaded,
        artboardValue,
        stateMachineValue,
        onStateMachineChange,
        onArtboardChange,
        onIsTouchScrollEnabledChange,
        onAutomaticallyHandleEventsChange,
        onAutoPlayChange,
        onFitChange,
        onAlignmentChange,
      })}
    >
      {children}
    </RiveContext.Provider>
  );
};

export const useRiveContext = () => {
  const context = useContext(RiveContext);
  if (!context) {
    throw new Error('useRiveContext must be used within a RiveProvider');
  }
  return context;
};
